import { styles } from "@repo/helpers";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@repo/ui";
import { ChevronDown, ChevronUp } from "lucide-react";
import { FC, useCallback, useState } from "react";

import { useSettingsApi } from "../../../api/settingsApi";
import { useVerbiageQuery } from "../../../api/verbiageApi";
import { useAssociationsInfoContext, useLocationContext } from "../../../contexts/editableItemContext";
import { useUserContext } from "../../../contexts/userContext";
import { Branches } from "./branches";
import style from "./style.module.scss";
import { ToggleButton } from "./toggle";
import { Props } from "./types";

export const Marketers: FC<{ readOnly?: boolean }> = ({ readOnly }) => {
	const { associations, filters } = useAssociationsInfoContext();
	const { marketerId, stateId, name } = filters;
	const nameFilter = name?.trim()?.toLowerCase();
	return (
		<div className={style.container}>
			{associations
				?.filter((m) => {
					const marketerMatches = !marketerId || m.marketerId == marketerId;
					const nameMatches =
						!nameFilter ||
						m.marketerName.toLowerCase().includes(nameFilter) ||
						m.mid.toLowerCase().includes(nameFilter) ||
						m.branches.some((b) => b.branchName.toLowerCase().includes(nameFilter));
					const stateMatches = !stateId || m.branches.some((b) => b.branchStateId == stateId);
					return marketerMatches && nameMatches && stateMatches;
				})
				?.map((m) => (
					<Marketer key={m.marketerId} locationMarketer={m} readOnly={readOnly} />
				))}
		</div>
	);
};

const Marketer: FC<Props> = ({ locationMarketer, readOnly }) => {
	const { useSettingsQuery } = useSettingsApi();
	const { locationId } = useLocationContext();
	const { user } = useUserContext();
	const { data: settings } = useSettingsQuery(user?.userId, locationId);
	const { userIsVendorAdmin } = user;
	const { office } = useVerbiageQuery();
	const { associations, updateMarketer } = useAssociationsInfoContext();

	const someMarketerEnabled = associations.some((a) => a.marketerEnabled);
	const { marketerId, marketerName, marketerEnabled, branches } = locationMarketer;

	const hasBranches = branches.length > 0;
	const [isOpen, setIsOpen] = useState(hasBranches);

	const buttonDisabled =
		(settings.limitMarketerAssociations && !marketerEnabled && someMarketerEnabled) ||
		userIsVendorAdmin ||
		readOnly;
	const hideCollapsible = settings.limitMarketerAssociations ? true : !hasBranches;
	const hideMarketer = settings.limitMarketerAssociations ? someMarketerEnabled && !marketerEnabled : false;

	const update = useCallback(
		(enabled: boolean) => {
			setIsOpen(hasBranches);
			updateMarketer(marketerId, enabled, settings.limitMarketerAssociations);
		},
		[marketerId, settings.limitMarketerAssociations, updateMarketer, hasBranches],
	);

	return (
		<Collapsible
			open={settings.limitMarketerAssociations ? marketerEnabled : isOpen}
			onOpenChange={setIsOpen}
			className={styles(marketerEnabled ? style.cardEnabled : style.card, hideMarketer && style.hidden)}
		>
			<div className={style.headerRow}>
				<div className={style.toggleContainer}>
					<ToggleButton enabled={marketerEnabled} update={update} disabled={buttonDisabled} />
					<h5 className={style.marketerName}>{marketerName}</h5>
				</div>
				<CollapsibleTrigger hidden={hideCollapsible}>
					{isOpen ? <ChevronUp size={36} /> : <ChevronDown size={36} />}
				</CollapsibleTrigger>
			</div>
			<CollapsibleContent className={style.content}>
				<h6 className={style.officeHeader}>{office}s</h6>
				<Branches locationMarketer={locationMarketer} readOnly={readOnly} />
			</CollapsibleContent>
		</Collapsible>
	);
};
